import { api } from "@/api/client";
import type { DataResponse, HranaPodlaVzoru, SlotAutoEdge } from "@/api/schemas";

/**
 * Preklad „hrúbka hrany v mm" → konkrétna hrana z ponuky slotu.
 *
 * Backend (`GET /sloty/{id}/auto-edge`) vyberá hranu podľa hrúbky **dielca**
 * (šírka pásky musí dielec prekryť) a požadovanej hrúbky pásky.
 *
 * Vytknuté z `DielecVisual.quickAssign`, aby hromadná úprava viacerých
 * dielcov používala presne ten istý preklad — pri zmiešaných slotoch sa
 * musí riešiť per dielec a dve implementácie by sa rozišli.
 *
 * Vracia `null`, až keď dekor nemá v potrebnej šírke **žiadnu** hranu.
 * Volajúci to musí ohlásiť — tiché preskočenie by znamenalo, že si user
 * myslí, že hranu nastavil.
 *
 * Keď dekor žiadanú hrúbku nemá, ale inú áno, backend vráti **najbližšiu
 * vyššiu** s `nahradena: true` (KOLO 103) — 0,5 sa v dekore bez 0,5 stane
 * 0,8, presne ako pri importe. Volajúci to má povedať hláškou, ale hranu
 * priradiť: dovtedy sa stav nezmenil vôbec a user videl, že *„sa mu to
 * vizuálne neprepne"*.
 */
export async function resolveAutoEdge(
  slotId: string,
  zMm: number | null,
  hranaMm: string,
): Promise<{
  id: string;
  nazov: string;
  /** Hrana má inú hrúbku než tú, ktorú user klikol. */
  nahradena: boolean;
  /** Skutočná hrúbka priradenej hrany (mm) — do hlášky. */
  hrubkaMm: number | null;
} | null> {
  const { data } = await api.get<DataResponse<SlotAutoEdge>>(
    `/sloty/${slotId}/auto-edge`,
    {
      params: {
        // Bez hrúbky dielca sa berie 18 mm — najčastejšia doska
        dielec_hrubka_mm: String(zMm && zMm > 0 ? zMm : 18),
        hrana_hrubka_mm: hranaMm,
      },
    },
  );
  const edge = data.data?.edge;
  if (!edge) return null;
  return {
    id: edge.id,
    nazov: edge.nazov,
    nahradena: data.data?.nahradena ?? false,
    hrubkaMm: edge.hrubka_mm != null ? parseFloat(String(edge.hrubka_mm)) : null,
  };
}

/**
 * Hrana toho istého typu ako `vzorId` pre dosku hrúbky `zMm` (KOLO 169).
 *
 * Hromadná úprava s vybraným typom hrany: dielce majú rôzne hrúbky, takže
 * každý potrebuje inú šírku pásky toho istého typu. Rodinu hrany aj šírku
 * rozhoduje backend (`GET /cennik/hrana-podla-vzoru`) — pravidlo sa do TS
 * neopisuje. Bez `hranaMm` dostane dielec hrúbku vzoru.
 *
 * `edge: null` znamená, že typ potrebnú šírku nemá v žiadnej hrúbke;
 * `sprava` povie prečo a volajúci ju má ukázať.
 */
export async function hranaPodlaVzoru(
  vzorId: string,
  zMm: number | null,
  hranaMm?: string,
): Promise<HranaPodlaVzoru> {
  const { data } = await api.get<DataResponse<HranaPodlaVzoru>>(
    "/cennik/hrana-podla-vzoru",
    {
      params: {
        vzor_id: vzorId,
        // Bez hrúbky dielca 18 mm — ako `resolveAutoEdge`
        dielec_hrubka_mm: String(zMm && zMm > 0 ? zMm : 18),
        ...(hranaMm ? { hrana_hrubka_mm: hranaMm } : {}),
      },
    },
  );
  return data.data;
}

/** Štyri strany dielca v poradí, v akom ich vidí user v nákrese. */
export const STRANY_HRAN = ["x1", "x2", "y1", "y2"] as const;
export type StranaHrany = (typeof STRANY_HRAN)[number];

/** Názvy polí dielca pre danú stranu — `hrana_x1_id` / `hrana_x1_auto`. */
export function polaHrany(strana: StranaHrany): {
  idKey: `hrana_${StranaHrany}_id`;
  autoKey: `hrana_${StranaHrany}_auto`;
} {
  return {
    idKey: `hrana_${strana}_id`,
    autoKey: `hrana_${strana}_auto`,
  };
}

/**
 * Znenie hlášky o náhradnej hrúbke — **jedno pre všetkých troch
 * volajúcich** (nákres dielca, mini-lišta hrán, hromadná úprava), aby
 * sa tri formulácie toho istého nerozišli.
 */
export function hlaskaNahrady(
  ziadanaMm: string,
  hrubkaMm: number | null,
): string {
  const skutocna = hrubkaMm != null ? `${hrubkaMm} mm` : "inú hrúbku";
  return `Hrana ${ziadanaMm} mm v tomto dekore nie je — použil som ${skutocna}.`;
}
